import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { format } from 'date-fns';

import ArchivedDimensionPreviewView from './ArchivedDimensionPreviewView';
import { useBudgetByDimensionReportContext } from './context/BudgetByDimensionReportContext';

import IconButton from 'CommonComponents/IconButton';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import ScopeChips from 'CommonComponents/ScopeChips';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import Tooltip from 'CommonComponents/Tooltip';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import usePagination from 'Hooks/usePagination';
import { useToast } from 'Hooks/useToast';

import DownloadIcon from 'Assets/icons/download.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import HourglassIcon from 'Assets/icons/hourglass.svg';
import InformationCircleIcon from 'Assets/icons/information-circle.svg';
import RefreshSyncIcon from 'Assets/icons/refresh-sync.svg';
import TrashIcon from 'Assets/icons/trash.svg';

// The archive carries three kinds of run, and each has its own action set:
// a background preview is viewed on screen and re-queued, an export is
// downloaded, and an emailed online review is opened or edited.
const FORMAT_PREVIEW = 'preview';
const FORMAT_ONLINE = 'online';

const COLUMN_COUNT = 10;

/**
 * Resolve the "Type" column label (Summary / Detailed / Side-by-side) from the
 * normalized reportType carried by every row family.
 */
const reportTypeLabel = (reportType) => {
    if (reportType === 'detailed_comparison') return $t('Side-by-side');
    if (reportType === 'detailed') return $t('Detailed');
    if (reportType === 'summary') return $t('Summary');
    return $t('n/a');
};

const formatDateTime = (value) => (value ? format(new Date(value), 'dd-MM-yyyy HH:mm') : '-');

const ArchiveBudgetByDimensionReport = () => {
    const {
        fetchArchivedReportsData,
        deleteArchivedReport,
        deleteBudgetReview,
        deleteBudgetReviewAction,
        refreshPreview,
        archivedReportsData,
        loadingArchivedReports,
        channelName,
        pruningDays,
    } = useBudgetByDimensionReportContext();

    const [rows, setRows] = useState(archivedReportsData ? archivedReportsData.data : []);
    const [viewingReport, setViewingReport] = useState(null);

    const pagination = usePagination({ perPage: 10 });

    const confirm = useConfirmationModal();

    const toast = useToast();

    useEffect(() => {
        fetchArchivedReportsData({
            page: pagination.page,
            perPage: pagination.perPage,
        });
    }, [pagination.page, pagination.perPage]);

    useEffect(() => {
        if (archivedReportsData) {
            setRows(archivedReportsData.data);
            pagination.setFrom(archivedReportsData.meta.from);
            pagination.setTo(archivedReportsData.meta.to);
            pagination.setTotal(archivedReportsData.meta.total);
            pagination.setLastPage(archivedReportsData.meta.last_page);
        }
    }, [archivedReportsData]);

    // Subscribe to WebSocket for real-time status updates
    useEffect(() => {
        if (!channelName || !window.Echo) {
            return;
        }

        const channel = window.Echo.private(channelName);

        channel.listen('.report.generated', (event) => {
            if (event.report) {
                fetchArchivedReportsData({
                    page: pagination.page,
                    perPage: pagination.perPage,
                });
            }
        });

        return () => {
            window.Echo.leave(channelName);
        };
    }, [channelName, fetchArchivedReportsData, pagination.page, pagination.perPage]);

    const handleReportDelete = useCallback(
        (reportFormat, reportId) => {
            confirm({
                title: $t('Delete Report'),
                message: $t('Are you sure you want to delete this report?'),
                onConfirm: () => {
                    if (reportFormat === FORMAT_ONLINE) {
                        deleteBudgetReview(reportId).then(() => {
                            fetchArchivedReportsData({
                                page: pagination.page,
                                perPage: pagination.perPage,
                            });
                        });
                        return;
                    }

                    deleteArchivedReport(reportId).then(() => {
                        // The open preview panel belongs to the row just removed.
                        setViewingReport((prev) => (prev && prev.id === reportId ? null : prev));

                        // If we are on a page greater than 1 and the total reports is 1, go back a page
                        if (pagination.page > 1 && pagination.total === 1) {
                            pagination.setPage(pagination.page - 1);
                        } else {
                            fetchArchivedReportsData({
                                page: pagination.page,
                                perPage: pagination.perPage,
                            });
                        }
                    });
                },
            });
        },
        [
            deleteArchivedReport,
            deleteBudgetReview,
            fetchArchivedReportsData,
            pagination.page,
            pagination.perPage,
            confirm,
            pagination.total,
            pagination.setPage,
        ],
    );

    const handleActionDelete = useCallback(
        (reviewId, actionId) => {
            confirm({
                title: $t('Delete Action'),
                message: $t('Are you sure you want to delete this action?'),
                onConfirm: () => {
                    deleteBudgetReviewAction(reviewId, actionId).then(() => {
                        fetchArchivedReportsData({
                            page: pagination.page,
                            perPage: pagination.perPage,
                        });
                    });
                },
            });
        },
        [deleteBudgetReviewAction, fetchArchivedReportsData, pagination.page, pagination.perPage, confirm],
    );

    const handleView = useCallback((report) => {
        setViewingReport((prev) => (prev && prev.id === report.id ? null : report));
    }, []);

    /**
     * Re-queue a preview from the criteria stored on its row, so the archive can
     * refresh a run without the user rebuilding those criteria. A new row
     * records the new run — the archive is a history, so a retry must not erase
     * the failure it is retrying.
     */
    const handleRefresh = useCallback(
        (report) => {
            refreshPreview(report.criteria)
                .then((result) => {
                    toast.success(result?.message ?? $t('Report generation has been queued. It will appear as soon as it is ready.'));

                    fetchArchivedReportsData({
                        page: pagination.page,
                        perPage: pagination.perPage,
                    });
                })
                .catch(() => {
                    toast.error($t('Failed to refresh report. Please try again.'));
                });
        },
        [refreshPreview, fetchArchivedReportsData, pagination.page, pagination.perPage, toast],
    );

    return (
        <div className="tw-flex tw-flex-col tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary tw-space-y-7 tw-h-full">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                <div className="tw-flex tw-flex-col tw-gap-y-1">
                    <div>
                        <h3 className="table__header">{$t('Archived Reports')}</h3>
                    </div>
                    <p className="tw-text-sm tw-text-charcoal-75">{$t('Reports older than {{days}} days are deleted automatically.', { days: pruningDays })}</p>
                    <div className="table__pagination-info">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination
                            lastPage={pagination.lastPage}
                            pageChange={(data) => pagination.setPage(+data.selected + 1)}
                            currentPage={pagination.page}
                        />
                    </div>
                </div>
                <div>
                    <PerPageSelect
                        value={pagination.perPage}
                        options={[10, 25, 50]}
                        onChange={(e) => {
                            pagination.setPerPage(parseInt(e.target.value));
                        }}
                    />
                </div>
            </div>
            <div className="inbud-table_wrapper tw-min-h-0 tw-overflow-auto">
                <table className="inbud-table">
                    <thead className="tw-sticky tw-top-0 tw-z-10">
                        <tr>
                            {/* Scope is fixed-width, User has a floor and dates stay on one line; the
                                rest wrap so all ten columns fit a standard desktop page. */}
                            <th>{$t('Date')}</th>
                            <th>{$t('Status')}</th>
                            <th className="tw-min-w-24">{$t('User')}</th>
                            <th>{$t('Name')}</th>
                            <th>{$t('Budget')}</th>
                            <th>{$t('Type')}</th>
                            <th className="tw-w-64">{$t('Scope')}</th>
                            <th>{$t('Recipient')}</th>
                            <th>{$t('Generated')}</th>
                            <th>{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loadingArchivedReports && <TableRowSkeleton rows={10} columns={COLUMN_COUNT} />}
                        {!loadingArchivedReports && rows.length === 0 && (
                            <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                                <td colSpan={COLUMN_COUNT} className="tw-p-4">
                                    <span>{$t('No data available')}</span>
                                </td>
                            </tr>
                        )}
                        {!loadingArchivedReports &&
                            rows.length > 0 &&
                            rows.map((report) => (
                                <React.Fragment key={`${report.id}-${report.date}-${report.statusKey}`}>
                                    {report.format === FORMAT_ONLINE ? (
                                        <Fragment>
                                            <OnlineReportRow report={report} onDelete={handleReportDelete} />
                                            {report.children &&
                                                report.children.length > 0 &&
                                                report.children.map((child) => (
                                                    <OnlineReportChildRow
                                                        key={`child-${child.actionId}-${child.statusKey}`}
                                                        report={child}
                                                        onDelete={handleActionDelete}
                                                    />
                                                ))}
                                        </Fragment>
                                    ) : (
                                        <ReportRow
                                            report={report}
                                            onDelete={handleReportDelete}
                                            onView={handleView}
                                            onRefresh={handleRefresh}
                                            viewing={!!viewingReport && viewingReport.id === report.id}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                    </tbody>
                </table>
            </div>

            {viewingReport && (
                <div className="table__card">
                    <ArchivedDimensionPreviewView report={viewingReport} />
                </div>
            )}
        </div>
    );
};

/**
 * The scope a run covered: the selected sports/venues as chips, or "All" when
 * the selection covers every one the budget holds.
 */
const ScopeCell = ({ report }) => (
    <td className="tw-w-64">
        <ScopeChips items={report.scopeNames ?? []} all={report.allScope} />
    </td>
);

/**
 * A background preview or a downloadable export.
 *
 * A preview has no .xlsx to hand over — its result lives in the preview cache —
 * so it is viewed on screen and re-queued instead of downloaded.
 */
const ReportRow = ({ report, onDelete, onView, onRefresh, viewing }) => {
    const { downloadArchivedReport } = useBudgetByDimensionReportContext();

    const isPreview = report.format === FORMAT_PREVIEW;
    const isComplete = report.statusKey === 'completed';
    const isFailed = report.statusKey === 'failed';
    const isInProgress = report.statusKey === 'in_progress' || report.statusKey === 'created';

    return (
        <tr>
            <td className="inbud-date-cell tw-whitespace-nowrap">{formatDateTime(report.date)}</td>
            <td>{report.statusLabel}</td>
            <td>{report.userName ?? '-'}</td>
            <td>{report.name ?? '-'}</td>
            <td>{report.budgetName ?? '-'}</td>
            <td className="tw-capitalize">{reportTypeLabel(report.reportType)}</td>
            <ScopeCell report={report} />
            <td className="tw-break-all">{report.reviewerEmail ?? $t('n/a')}</td>
            <td className="inbud-date-cell tw-whitespace-nowrap">{formatDateTime(report.generatedAt)}</td>
            <td>
                <div className="tw-flex tw-items-center">
                    {isComplete && isPreview && (
                        <IconButton onClick={() => onView(report)} variant="primary" size="sm" ghost title={viewing ? $t('Hide') : $t('View')}>
                            <EyeIcon className="tw-w-3.5 tw-h-3.5" />
                        </IconButton>
                    )}
                    {isComplete && !isPreview && (
                        <IconButton onClick={() => downloadArchivedReport(report.id)} variant="success" size="sm" ghost title={$t('Download')}>
                            <DownloadIcon className="tw-w-3.5 tw-h-3.5" />
                        </IconButton>
                    )}
                    {isFailed && <FailedIcon message={report.failureMessage} />}
                    {isInProgress && <InProgressIcon />}
                    {isPreview && !isInProgress && (
                        <IconButton onClick={() => onRefresh(report)} variant="primary" size="sm" ghost title={$t('Refresh')}>
                            <RefreshSyncIcon className="tw-w-3.5 tw-h-3.5" />
                        </IconButton>
                    )}
                    {report.canBeDeleted && (
                        <IconButton onClick={() => onDelete(report.format, report.id)} variant="danger" size="sm" ghost title={$t('Delete')}>
                            <TrashIcon className="tw-w-3 tw-h-3" />
                        </IconButton>
                    )}
                </div>
            </td>
        </tr>
    );
};

// Focus as well as hover: a tap or a Tab focuses the trigger, which opens it.
// Not click — the hook toggles on click, so a click after hover would close it.
const ICON_TOOLTIP_TRIGGER = ['hover', 'focus'];

/**
 * Names an icon-only link or button. The child must already be focusable and
 * keeps its own onClick; it gets the label as its accessible name.
 */
const ActionTooltip = ({ label, children }) => (
    <Tooltip placement="left" allowTooltip tooltipType="text" trigger={ICON_TOOLTIP_TRIGGER} tooltipContent={label}>
        {React.cloneElement(children, { 'aria-label': label })}
    </Tooltip>
);

const OnlineReportRow = ({ report, onDelete }) => {
    const { showBudgetReviewUrl, editBudgetReviewUrl } = useBudgetByDimensionReportContext();

    return (
        <tr className="tw-bg-grey-50">
            <td className="inbud-date-cell tw-whitespace-nowrap">{formatDateTime(report.date)}</td>
            <td>{report.statusLabel}</td>
            <td>{report.userName ?? '-'}</td>
            <td>{report.name ?? '-'}</td>
            <td>{report.budgetName ?? '-'}</td>
            <td className="tw-capitalize">{reportTypeLabel(report.reportType)}</td>
            <ScopeCell report={report} />
            <td className="tw-break-all">{report.reviewerEmail ?? $t('n/a')}</td>
            <td className="inbud-date-cell tw-whitespace-nowrap">{formatDateTime(report.generatedAt)}</td>
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <ActionTooltip label={$t('Show Budget Review')}>
                        <a href={showBudgetReviewUrl.replace('BUDGETS_REVIEW_ID', report.id)} target="_blank" rel="noopener noreferrer">
                            <EyeIcon className="tw-h-4 tw-w-4 tw-text-blue" />
                        </a>
                    </ActionTooltip>
                    {report.canBeEdited && (
                        <ActionTooltip label={$t('Edit Review')}>
                            <a href={editBudgetReviewUrl.replace('BUDGETS_REVIEW_ID', report.id)} target="_blank" rel="noopener noreferrer">
                                <EditIcon className="tw-h-4 tw-w-4 tw-text-green" />
                            </a>
                        </ActionTooltip>
                    )}
                    {report.canBeDeleted && (
                        <ActionTooltip label={$t('Delete')}>
                            <button type="button" onClick={() => onDelete(report.format, report.id)}>
                                <TrashIcon className="tw-w-4 tw-h-4 tw-text-red" />
                            </button>
                        </ActionTooltip>
                    )}
                </div>
            </td>
        </tr>
    );
};

const OnlineReportChildRow = ({ report, onDelete }) => {
    const { showBudgetReviewUrl } = useBudgetByDimensionReportContext();

    return (
        <tr className="!tw-bg-white">
            <td className="inbud-date-cell tw-whitespace-nowrap">{formatDateTime(report.date)}</td>
            <td className="!tw-pl-4">{report.statusLabel}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <ActionTooltip label={$t('Show Budget Review')}>
                        <a href={showBudgetReviewUrl.replace('BUDGETS_REVIEW_ID', report.id)} target="_blank" rel="noopener noreferrer">
                            <EyeIcon className="tw-h-4 tw-w-4 tw-text-blue" />
                        </a>
                    </ActionTooltip>
                    {report.canBeDeleted && (
                        <ActionTooltip label={$t('Delete')}>
                            <button type="button" onClick={() => onDelete(report.id, report.actionId)}>
                                <TrashIcon className="tw-w-4 tw-h-4 tw-text-red" />
                            </button>
                        </ActionTooltip>
                    )}
                </div>
            </td>
        </tr>
    );
};

/**
 * Why a run failed, on the icon itself. The message arrives sanitised — the raw
 * reason the generator stages can carry a failing SQL statement — so it is
 * rendered as it came, with the generic line as the fallback for a row whose
 * generator recorded nothing.
 */
const FailedIcon = ({ message }) => {
    // Must stay the sanitised PreviewFailureMessage text: TextTooltip renders a
    // string as HTML, so the raw `_failure_reason` must never reach it.
    const text = message ?? $t('The report could not be generated. Please try again, and contact support if it keeps failing.');

    return (
        <Tooltip placement="left" allowTooltip tooltipType="text" trigger={ICON_TOOLTIP_TRIGGER} tooltipContent={text}>
            <div className="tw-flex tw-justify-center tw-items-center tw-w-6 tw-h-6 tw-rounded-full" tabIndex={0} role="img" aria-label={text}>
                <InformationCircleIcon className="tw-w-3.5 tw-h-3.5 tw-text-red" />
            </div>
        </Tooltip>
    );
};

const InProgressIcon = () => {
    const text = $t('In Progress');

    return (
        <Tooltip placement="left" allowTooltip tooltipType="text" trigger={ICON_TOOLTIP_TRIGGER} tooltipContent={text}>
            <div className="tw-flex tw-justify-center tw-items-center tw-w-6 tw-h-6 tw-rounded-full" tabIndex={0} role="img" aria-label={text}>
                <HourglassIcon className="tw-w-3.5 tw-h-3.5 tw-text-yellow" />
            </div>
        </Tooltip>
    );
};

export default ArchiveBudgetByDimensionReport;
